
import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import { getToken } from "../../utils/auth";

// 1. Types
// DELETE the first ThemeColors and keep only this merged version:
export interface IGradient {
  from: { color: string; opacity: number };
  to: { color: string; opacity: number };
}

export interface IBranding {
  name?: string | null;
  logo: string | null;
  visibility: { device: boolean; user: boolean };
}

export interface IAssets {
  banners: { device: string | null; user: string | null };
}

export interface IColors {
  primary: string | null;
  secondary: string | null;
  supporting: string | null;
  icon: string | null;
  iconBackground: string | null;
  appBackground: string | null;
  card: string | null;
  popup: string | null;
  headingText: string | null;
  bodyText: string | null;
  mutedText: string | null;
  errorText: string | null;
}

export interface IGradientColors {
  icon: IGradient | null;
  iconBackground: IGradient | null;
  deviceBackground: IGradient | null;
  card: IGradient | null;
  popup: IGradient | null;
  progressBar: IGradient | null;
  userBackground: IGradient | null;
}

export interface IStrokeColors {
  card: string | null;
  popup: string | null;
  progressBar: string | null;
}

export interface IThemeMode {
  branding: IBranding;
  colors: IColors;
  gradients: IGradientColors | null;
  strokes: IStrokeColors | null;
  assets: IAssets;
}

export interface ThemeSettings {
  _id: string;
  name: string;
  organisation: string;
  mode: "LIGHT" | "DARK" | "BOTH";
  defaultMode: "LIGHT" | "DARK" | "AUTO";
  lightTheme: IThemeMode | null;
  darkTheme: IThemeMode | null;
  status: "ACTIVE" | "INACTIVE" | "Active" | "Inactive";
}

export interface ThemeDocument extends ThemeSettings {
  _id: string;
  access: string;
  subscriber: string;
  createdAt: string; // ISO Date string from DB
  updatedAt: string; // ISO Date string from DB
}

export interface Theme {
  _id: string;
  name: string;
  access: "ORGANISATION" | "SUBSCRIBER" | string; // Based on your "ORGANISATION" value
  type: "CUSTOM" | "DEFAULT" | string;
  organisation: string; // This holds the ID "69e1f240..."
  subscriber: string;
  mode: "BOTH" | "DARK" | "LIGHT"; 
  defaultMode: "DARK" | "LIGHT" | "AUTO";
  status: "Active" | "Inactive" | string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Theme objects - using the interfaces you defined earlier in the slice
  lightTheme: IThemeMode | null;
  darkTheme: IThemeMode | null;
}





// 2. API Slice
export const themeApi = createApi({
  reducerPath: "themeApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Theme", "Themes"],

  endpoints: (builder) => ({
    

    getDefaultTheme: builder.query<ThemeDocument, void>({
      query: () => ({
        // Adjust this if you use your ApiEndpoints constants instead
        url: `/subscriber-service/api/v1/theme/default`, 
        method: "GET",
      }),
      transformResponse: (res: any) => {
        // Extracts the theme object based on standard Express/Mongoose responses
        return res?.result ?? res?.data ?? res;
      },
      // Tagging it with "Themes" ensures this cache automatically refreshes 
      // if a different theme is saved/toggled to active!
      providesTags: ["Themes"], 
    }),

    updateTheme: builder.mutation<ThemeDocument, { id: string; body: Partial<ThemeSettings> }>({
      query: ({ id, body }) => ({
        url: `/subscriber-service/api/v1/theme/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res: any) => {
        return res?.result ?? res?.data ?? res;
      },
      invalidatesTags: ["Themes"],
    }),

    uploadThemeLogo: builder.mutation<
  any,
  {
    file: File | Blob;
    onUploadProgress?: (progress: number) => void;
  }
>({
  async queryFn(arg) {
    const { file, onUploadProgress } = arg;

    const form = new FormData();

    form.append(
      "image",
      file,
      (file as File)?.name || "theme-logo.png"
    );

    return await new Promise<any>((resolve) => {
      try {
        const xhr = new XMLHttpRequest();

        const base = (ApiEndpoints as any).serverURL || "";

        const url = `${base}/file-service/api/v1/file/theme/app`;

        xhr.open("POST", url);

        const token = getToken();

        if (token) {
          xhr.setRequestHeader(
            "Authorization",
            `Bearer ${token}`
          );
        }

        if (
          xhr.upload &&
          typeof onUploadProgress === "function"
        ) {
          xhr.upload.onprogress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
              const percent = Math.round(
                (e.loaded / e.total) * 100
              );

              try {
                onUploadProgress(percent);
              } catch {
                //
              }
            }
          };
        }

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) return;

          const ct =
            xhr.getResponseHeader("content-type") || "";

          let parsed: any = null;

          try {
            parsed = ct.includes("application/json")
              ? JSON.parse(xhr.responseText)
              : xhr.responseText;
          } catch {
            parsed = xhr.responseText;
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            toast.success("Theme logo uploaded");

            resolve({ data: parsed });
          } else {
            toast.error(
              parsed?.message || "Upload failed"
            );

            resolve({
              error: {
                status: xhr.status,
                data: parsed,
              },
            });
          }
        };

        xhr.onerror = () => {
          toast.error("Upload network error");

          resolve({
            error: {
              status: "NETWORK_ERROR",
              data: null,
            },
          });
        };

        xhr.send(form);
      } catch (err: any) {
        toast.error("Upload error");

        resolve({
          error: {
            status: "XHR_ERROR",
            data: err?.message ?? err,
          },
        });
      }
    });
  },
}),

  }),
});

// 3. Export Hooks
export const {
  useGetDefaultThemeQuery,
  useUpdateThemeMutation,
  useUploadThemeLogoMutation,
} = themeApi;